import { Box, Stack } from '@mui/material'
import { CloudUploadOutlined, MessageOutlined, ShoppingCartOutlined } from '@mui/icons-material'
import { Dispatch, FC, SetStateAction, useMemo } from 'react'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { GalleryTab, useGalleryAssignment, useGalleryDeal, useGalleryProduct, useGalleryVirtualVisit } from '../../contexts'

import { AssignmentStage } from 'constants/assignment'
import AutoFixNormalOutlinedIcon from '@mui/icons-material/AutoFixNormalOutlined'
import { ClientAssignmentReference } from '../../../../Property/ClientProperty/ClientAssignmentReference'
import { ClientGalleryDocuments } from '../ClientGalleryDocuments'
import { ClientGalleryPhoto } from '../ClientGalleryPhoto'
import { ClientGalleryVirtualVisit } from '../ClientGalleryVirtualVisit'
import { DocumentUploadType } from 'constants/documents'
import { EmptyContent } from 'components/common/EmptyContent'
import { GRAY_800 } from 'constants/styling/theme'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import { MUIButton } from 'components/common/MUIButton'
import { Path } from 'constants/router'
import { useClientGallery } from '../_main'
import { useClientProperty } from 'components/pages/Property/ClientProperty/_main/contexts'
import { useGetAssignmentDocuments } from 'dataQueries'
import { useAuth0 } from 'utils/auth'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @interface Props
 */
interface Props {
  /** Set current active tab */
  setActiveTab: Dispatch<SetStateAction<GalleryTab>>
}

/**
 * Client gallery content.
 *
 * @example <ClientGalleryContent />
 */
export const ClientGalleryContent: FC<Props> = ({ setActiveTab }) => {
  const { t } = useTranslation(['gallery'])
  const navigate = useNavigate()


  const { isVirtualVisit, isDocumentProduct, isAIEditingProduct } = useGalleryProduct()
  const { virtualVisitURL } = useGalleryVirtualVisit()
  const { requiresPowerOfAttorneyDocuments, totalRequestCountData } = useGalleryDeal()
  const { assignmentId } = useClientGallery()
  const { userPropertyWorkspaceId } = useUserData()
  const { roles } = useAuth0()
  const {
    assignmentStage,
    isDocumentWithValidateDraft,
    isVisualSendToClient,
    isDocSendToClient,
    isWaitingPoADoc,
    isDocumentInStageProgress,
    isAssignmentCancelled,
  } = useGalleryAssignment()
  const { propertyData } = useClientProperty()
  const showCancelledLines = useFeatureFlag(FeatureFlag.ENABLE_SF_LAST_LINE_CANCEL)

  /** A cancelled line sits at its terminal stage but was never delivered */
  const isCancelledContent = showCancelledLines && isAssignmentCancelled

  const getAssignmentOutputDocuments = useGetAssignmentDocuments(assignmentId, DocumentUploadType.OUTPUT, userPropertyWorkspaceId, roles)
  const assignmentDocumentsData = useMemo(() => getAssignmentOutputDocuments.data?.data || [], [getAssignmentOutputDocuments.data])

  const isAIEditingInProcess = useMemo(() => isAIEditingProduct && assignmentStage === AssignmentStage.VISUALS_SENT_BY_BKBN_TO_EDITOR, [assignmentStage, isAIEditingProduct])

  const isShowDefaultInProgressContent = useMemo(() => {
    if (isVisualSendToClient) return false
    if (isDocSendToClient || isWaitingPoADoc) return false
    if (isDocSendToClient || isDocumentWithValidateDraft) return false
    if (isAIEditingInProcess) return false
    if (!isDocSendToClient && !!assignmentDocumentsData.length) return false

    return true
  }, [assignmentDocumentsData.length, isAIEditingInProcess, isDocSendToClient, isDocumentWithValidateDraft, isVisualSendToClient, isWaitingPoADoc])

  const hasMessageRequests = useMemo(() => {
    if (!isDocumentProduct) return false
    if (!isDocumentInStageProgress) return false
    if (isDocSendToClient) return false

    const hasOpenRequests = !!totalRequestCountData && totalRequestCountData > 0

    return hasOpenRequests && assignmentStage === AssignmentStage.WAITING_FOR_CLIENT_TO_UPLOAD_DOCUMENTS
  }, [assignmentStage, isDocSendToClient, isDocumentInStageProgress, isDocumentProduct, totalRequestCountData])

  return (
    <Stack width="100%">
      {/** ASSIGNMENT CANCELLED - replaces every delivered / in-progress branch */}
      {isCancelledContent && (
        <Box maxWidth="60rem" width="100%" margin="auto" marginTop="8rem">
          <EmptyContent
            title={t('client_gallery.cancelled')}
            icon={<ShoppingCartOutlined sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
            button={
              <MUIButton
                sx={{ marginTop: '2.4rem' }}
                onClick={() => navigate(Path.ORDERS)}
              >
                {t('client_gallery.cancelled_action')}
              </MUIButton>
            }
          />
        </Box>
      )}

      {!isCancelledContent && (
        <>
          {/** ASSIGNMENT NOT DELIVERED TO CLIENT */}
          {isShowDefaultInProgressContent && !hasMessageRequests && (
            <Box maxWidth="60rem" width="100%" margin="auto" marginTop="8rem">
              <EmptyContent
                title={t('client_gallery.empty')}
                icon={<ShoppingCartOutlined sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
                button={
                  <MUIButton
                    sx={{ marginTop: '2.4rem' }}
                    onClick={() => setActiveTab(GalleryTab.ORDER_INFO)}
                  >
                    {t('client_gallery.empty_action')}
                  </MUIButton>
                }
              />
            </Box>
          )}

          {/** ASSIGNMENT NOT DELIVERED TO CLIENT AND HAS REQUESTS. Documents */}
          {hasMessageRequests &&
            <Box maxWidth="60rem" width="100%" margin="auto" marginTop="8rem">
              <EmptyContent
                title={t('client_gallery.available_requests.title')}
                description={t('client_gallery.available_requests.description')}
                icon={<MessageOutlined sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
                button={
                  <MUIButton
                    sx={{ marginTop: '2.4rem' }}
                    onClick={() => setActiveTab(GalleryTab.REQUESTS)}
                  >
                    {t('client_gallery.available_requests.see_request')}
                  </MUIButton>
                }
              />
            </Box>
          }

          {/** ASSIGNMENT NOT DELIVERED TO CLIENT - AI EDITING */}
          {isAIEditingInProcess &&
            <Box maxWidth="60rem" width="100%" margin="auto" marginTop="8rem">
              <EmptyContent
                title={t('ai_editing_empty_content:title')}
                description={t('ai_editing_empty_content:description')}
                icon={<AutoFixNormalOutlinedIcon sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
                button={
                  <MUIButton
                    type="secondaryBorder"
                    sx={{ marginTop: '2.4rem' }}
                    onClick={() => navigate(Path.INDEX)}
                    startIcon={<HomeOutlinedIcon sx={{ color: GRAY_800, fontSize: '2rem' }} />}
                  >
                    {t('ai_editing_empty_content:button')}
                  </MUIButton>
                }
              />
            </Box>
          }

          {/** ASSIGNMENT WAITING FOR PoA UPLOAD. Documents */}
          {isDocumentProduct && isWaitingPoADoc && requiresPowerOfAttorneyDocuments &&
            <Box maxWidth="60rem" width="100%" margin="auto" marginTop="8rem">
              <EmptyContent
                title={t('client_gallery.empty_waiting_power_of_attorney')}
                description={t('client_gallery.empty_waiting_power_of_attorney_description')}
                icon={<CloudUploadOutlined sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
                button={
                  <MUIButton
                    sx={{ marginTop: '2.4rem' }}
                    onClick={() => setActiveTab(GalleryTab.ORDER_INFO)}
                  >
                    {t('client_gallery.empty_waiting_power_of_attorney_action')}
                  </MUIButton>
                }
              />
            </Box>
          }

          {/** ASSIGNMENT DELIVERED TO CLIENT. Photo, staging, video... etc */}
          {isVisualSendToClient && !isVirtualVisit && !isDocumentProduct && <ClientGalleryPhoto propertyData={undefined} />}

          {/** ASSIGNMENT DELIVERED TO CLIENT. Matterport - Virtual visit */}
          {isVisualSendToClient && isVirtualVisit && !!virtualVisitURL && (
            <Stack marginTop="3.2rem" width="100%" position="relative" display="flex">
              <ClientAssignmentReference />

              <ClientGalleryVirtualVisit />
            </Stack>
          )}

          {/** ASSIGNMENT DELIVERED TO CLIENT. Documents */}
          {(isDocSendToClient || isDocumentWithValidateDraft || !!assignmentDocumentsData?.length) && isDocumentProduct && <ClientGalleryDocuments assignmentId={assignmentId} setActiveTab={setActiveTab} propertyData={propertyData} />}
        </>
      )}
    </Stack>
  )
}
